/**
 * Stage decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const stageDecorationsPack: ToilePack = {
    slug: 'stage-decorations',
    category: 'occasions',
    name: 'Stage decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'school', label: 'School stage', subject: 'a school stage with a marigold border and a banner reading "ANNUAL DAY"'},
        {key: 'wedding', label: 'Wedding stage', subject: 'a wedding stage with a floral arch and sofa'},
        {key: 'event', label: 'Event stage', subject: 'an event stage with drapes and lights'},
    ],
    styles: [
        {key: 'budget', label: 'Budget', style: 'a simple low-budget setup of cloth drapes, paper flowers and a few marigold strings, seen straight on from the middle of the hall', palette: 'white cloth with orange and yellow flowers', framing: 'the stage centered, the first row of chairs in front'},
        {key: 'grand', label: 'Grand', style: 'a lavish setup with layered fresh flowers, crystal chandeliers and a carpeted runway, a wide view from the aisle', palette: 'ivory, gold and deep red', framing: 'the stage filling the upper two thirds, the runway leading in'},
        {key: 'outdoor', label: 'Outdoor', style: 'set outdoors on a green lawn in bright afternoon, a fabric canopy over the stage, a three-quarter view', palette: 'flower colours against green grass and pale sky', framing: 'the stage on one side, the lawn opening out on the other'},
    ],
    directions: ['Diwali stage', 'farewell party', 'blue and white'],
    directionHint: 'try: Diwali stage',
    seo: {
        title: 'Stage decoration: school, wedding, event stage',
        metaDescription: 'Nine stage decoration pictures for a school, a wedding or an event: a budget stage, a grand stage and an outdoor stage, seen from the hall.',
        h1: 'Stage decoration: school, wedding, event stage',
        tagline: 'Nine pictures, three stages, each in a budget, a grand and an outdoor setting.',
        intro: 'Stage decoration for a school, a wedding or an event: a school stage with a marigold border and an ANNUAL DAY banner, a wedding stage with a floral arch and a sofa, and an event stage with drapes and lights. Each comes as a budget set of cloth drapes and paper flowers, a grand set of layered flowers and chandeliers, or an outdoor set on a lawn. Type a twist, such as Diwali, and the same stages carry it.',
        useCases: [
            'a school annual day stage',
            'a wedding stage for a family function',
            'a farewell or event stage in a hall',
            'an outdoor stage on a lawn',
        ],
        faq: [
            {q: 'What is the budget stage?', a: 'Cloth drapes, paper flowers and a few marigold strings, seen straight on from the middle of the hall.'},
            {q: 'Are the seats filled?', a: 'No. The seats in the pictures are empty.'},
            {q: 'Does any stage show a banner?', a: 'Yes. The school stage shows an ANNUAL DAY banner. The other pictures have no lettering.'},
            {q: 'Can I ask for a different occasion?', a: 'Yes. Type a twist such as Diwali stage or blue and white, and the same three stages carry it.'},
        ],
    },
}
